import {useEffect, useState} from "react"
import styles from "./DocumentType.module.css"
import {Checkbox, Col, Row} from "antd"
import {FormLabel} from "components/Form"
import {BaseButton, BaseInput} from "components"
import DepartmentSubunitSelect from "components/DepartmentSubunitSelect"
import {useAllDocumentTypes, useModel, useVisible} from "hooks"
import {KlassDropdown} from "components/Select"
import {DocumentType as DocumentTypeType} from "types/settings/documentType"

export type DocumentTypeItem = {
    documentName?: string
    description?: string
    departmentSubunit?: any
    isVisibleForUser?: boolean
    notifyStudent?: boolean
    presetDocumentType?: DocumentTypeType
    isOriginal?: boolean
    isApproved?: boolean
}

type DocumentTypeProps = {
    accessibleSubunitIds: number[]
    onApply: (documentType: DocumentTypeItem) => void
}

const INITIAL_DOCUMENT_INFO: DocumentTypeItem = {
    documentName: "",
    description: "",
    departmentSubunit: null,
    isApproved: false
}

export function DocumentType(props: DocumentTypeProps) {
    const {onApply, accessibleSubunitIds} = props
    const [documentType, setDocumentType] = useState<DocumentTypeItem>(INITIAL_DOCUMENT_INFO)
    const model = useModel()
    const isStaffOrAdmin = model.isStaffOrAdmin()
    const {documentTypes: presetDocumentTypes} = useAllDocumentTypes()
    const toggleDocumentType = useVisible(false)

    useEffect(() => {
        setDocumentType({...INITIAL_DOCUMENT_INFO, isVisibleForUser: !isStaffOrAdmin, isOriginal: !isStaffOrAdmin})
    }, [isStaffOrAdmin])

    const handleFieldChange = (key: keyof DocumentTypeItem, value: any) => {
        const _documentType = {...documentType, [key]: value}
        setDocumentType(_documentType)
    }

    const onApplyTemplate = () => {
        onApply(documentType)
    }

    return (
        <div className={styles.container}>
            <div
                className={styles.toggleWrap}
                onClick={() => {
                    toggleDocumentType.toggle()
                }}>
                <Checkbox checked={toggleDocumentType.isVisible} />
                <span className={styles.title}>APPLY TO ALL DOCUMENTS BELOW</span>
            </div>
            {toggleDocumentType.isVisible && (
                <div className={styles.contentContainer}>
                    <Row gutter={[12, 12]}>
                        <Col span={isStaffOrAdmin ? 5 : 8}>
                            <FormLabel label="Document Name" />
                            <KlassDropdown
                                creatable
                                placeholder="Select or enter name"
                                formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                                isValidNewOption={(inputValue: string) => !!inputValue}
                                getNewOptionData={(inputValue: string) => ({
                                    documentTypeId: 0,
                                    name: inputValue
                                })}
                                options={presetDocumentTypes}
                                valueKey="documentTypeId"
                                labelKey="name"
                                value={
                                    documentType.presetDocumentType
                                        ? presetDocumentTypes.find(
                                              (x) => x.documentTypeId === documentType.presetDocumentType.documentTypeId
                                          ) ?? {
                                              documentTypeId: 0,
                                              name: documentType.presetDocumentType.name
                                          }
                                        : undefined
                                }
                                onChange={(option: DocumentTypeType) => {
                                    let _documentType: DocumentTypeItem = {
                                        ...documentType,
                                        presetDocumentType: option,
                                        documentName: option?.name
                                    }
                                    if (option && option.documentTypeId !== 0) {
                                        _documentType.description = option.description
                                        _documentType.departmentSubunit = option.departmentSubunit
                                    }
                                    setDocumentType(_documentType)
                                }}
                            />
                        </Col>
                        <Col span={isStaffOrAdmin ? 5 : 10}>
                            <FormLabel label="Description" />
                            <BaseInput
                                placeholder="description"
                                value={documentType.description}
                                onChange={(v) => handleFieldChange("description", v)}
                            />
                        </Col>
                        <Col span={6}>
                            <FormLabel label="Department" />
                            <DepartmentSubunitSelect
                                subunitIds={accessibleSubunitIds}
                                placeholder="department"
                                value={documentType.departmentSubunit}
                                onChange={(v) => handleFieldChange("departmentSubunit", v)}
                            />
                        </Col>
                        {isStaffOrAdmin && (
                            <>
                                <Col span={2}>
                                    <FormLabel label="Visible to user" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={documentType.isVisibleForUser}
                                            onChange={(v) => handleFieldChange("isVisibleForUser", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                                <Col span={2}>
                                    <FormLabel label="Notify student" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={documentType.notifyStudent}
                                            onChange={(v) => handleFieldChange("notifyStudent", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                                <Col span={2}>
                                    <FormLabel label="Original" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={documentType.isOriginal}
                                            onChange={(v) => handleFieldChange("isOriginal", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                                <Col span={2}>
                                    <FormLabel label="Approved" className={styles.visible} />
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={documentType.isApproved}
                                            onChange={(v) => handleFieldChange("isApproved", v.target.checked)}
                                        />
                                    </div>
                                </Col>
                            </>
                        )}
                    </Row>
                    <div className={styles.action}>
                        <BaseButton title="Apply for all" onClick={onApplyTemplate} />
                    </div>
                </div>
            )}
        </div>
    )
}
